<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { UIcon } from "#components";
import type { SkillWithMetrics } from "~/composables/useSkillMetrics";
import { getTier, SkillTier } from "~/composables/useSkillCategories";
import type { PillColor } from "~/components/ui/Pill/Pill.vue";
import { useI18n } from "~/composables/useI18n";

const t = useI18n();
const router = useRouter();

interface SkillCardProps {
  skill: SkillWithMetrics;
}

const props = defineProps<SkillCardProps>();

const tier = computed<SkillTier>(() => getTier(props.skill.expertise));

const tierPillColor = computed((): PillColor => {
  const map: Record<SkillTier, PillColor> = {
    [SkillTier.Expert]: "emerald",
    [SkillTier.Proficient]: "blue",
    [SkillTier.Familiar]: "gray",
  };
  return map[tier.value];
});

const tierLabel = computed(() => t(`skillsPage.tiers.${tier.value}`));

const hasProjects = computed(() => props.skill.projectCount > 0);

const projectsLabel = computed(() =>
  t("skillsPage.projectsCount")(props.skill.projectCount)
);

const navigate = () => {
  if (hasProjects.value && props.skill.slug) {
    router.push(`/skills/${props.skill.slug}`);
  }
};
</script>

<template>
  <article
    class="bg-white dark:bg-primary-900 border border-gray-100 dark:border-primary-800 rounded-lg p-4 flex flex-col gap-2 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
    :class="hasProjects ? 'cursor-pointer' : ''"
    @click="navigate"
  >
    <!-- Header: icon + name -->
    <div class="flex items-center gap-2">
      <UIcon
        v-if="skill.isIconFont && skill.icon.startsWith('i-custom-')"
        :name="skill.icon"
        mode="svg"
        class="text-xl flex-shrink-0 text-primary-600 dark:text-primary-300"
      />
      <span
        v-else-if="skill.isIconFont"
        :class="[
          skill.icon,
          'text-xl flex-shrink-0 text-primary-600 dark:text-primary-300',
        ]"
        aria-hidden="true"
      />
      <UIcon
        v-else
        :name="skill.icon"
        class="text-xl flex-shrink-0 text-primary-600 dark:text-primary-300"
      />
      <span
        class="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight"
      >
        {{ skill.name }}
      </span>
    </div>

    <!-- Badges: years + tier -->
    <div class="flex flex-wrap items-center gap-1.5">
      <UiPill color="primary">{{ skill.yearsDisplay }}</UiPill>
      <UiPill :color="tierPillColor">{{ tierLabel }}</UiPill>
    </div>

    <!-- Used in: N projects hint -->
    <p
      v-if="hasProjects"
      class="text-xs text-primary-600 dark:text-primary-400"
    >
      {{ t("skillsPage.projectsExpandLabel") }}: {{ projectsLabel }}
    </p>
  </article>
</template>
